import { AND, Combinator, EQUAL, Filter, HAS, IN, NOT_CONTAINS, NOT_EQUAL, NOT_HAS, OR, SingleFilter } from "@dto/records";
import { put } from "./utils";
import { View, Group } from "@dto/views";
import { BASE_URL, PATH_SEPARATOR, SOURCE_SEPARATOR } from "./constants";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { useModelStore, useUserStore } from "src/store";
import Field, { AggregationType, FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { USER_FORMULA } from "./users";
import { isArray } from "./types";


// IsSingleFilter checks if the filter is a single filter
// A single filter is a filter that does not contain a group filter
export function isSingleFilter(filter: Filter): boolean {
    return (filter.groupFilter === null || filter.groupFilter === undefined) && (filter.singleFilter !== null && filter.singleFilter !== undefined)
}

// IsGroupFilter checks if the filter is a group filter
// A group filter is a filter that contains a group filter
export function isGroupFilter(filter: Filter, combinator: Combinator): boolean {
    return isAnyGroupFilter(filter) && filter.groupFilter!.combinator === combinator
}

export function isAnyGroupFilter(filter: Filter): boolean {
    return (filter.groupFilter !== null && filter.groupFilter !== undefined) && (filter.singleFilter === null || filter.singleFilter === undefined)
}

export function cleanFilter(filter: Filter | null): Filter | null {

    if (!filter || (!isAnyGroupFilter(filter) && !isSingleFilter(filter))) {
        return null;
    }

    if (isSingleFilter(filter)) {
        if (!filter.singleFilter!.pathKey || !filter.singleFilter!.operator) {
            return null;
        }

        if (filter.singleFilter!.value != null && filter.singleFilter!.valueKey != null) {
            return {
                groupFilter: null,
                singleFilter: {
                    pathKey: filter.singleFilter!.pathKey,
                    operator: filter.singleFilter!.operator,
                    value: null,
                    valueKey: filter.singleFilter!.valueKey,
                    formula: null
                }
            }
        }
        return filter;
    }

    if (isAnyGroupFilter(filter)) {

        if (!filter.groupFilter!.combinator) {
            return null;
        }

        if (!filter.groupFilter!.filters || filter.groupFilter!.filters.length === 0) {
            return null;
        }

        return {
            singleFilter: null,
            groupFilter: {
                combinator: filter.groupFilter!.combinator,
                filters: filter.groupFilter!.filters.map(f => cleanFilter(f)).filter(f => f != null) as Filter[]
            }
        }
    }
    return null;
}

// cleanFilterFromCondition cleans the filter from the given condition
// It returns the filter that match the condition
export function cleanFilterFromCondition(filter: Filter | null, condition: (singleFilter: SingleFilter) => boolean): Filter | null {

    if (!filter || (!isAnyGroupFilter(filter) && !isSingleFilter(filter))) {
        return null;
    }

    if (isSingleFilter(filter)) {
        if (condition(filter.singleFilter!)) {
            return filter;
        }
        return null;
    }

    if (isAnyGroupFilter(filter)) {

        if (!filter.groupFilter!.combinator) {
            return null;
        }

        if (!filter.groupFilter!.filters || filter.groupFilter!.filters.length === 0) {
            return null;
        }

        return {
            singleFilter: null,
            groupFilter: {
                combinator: filter.groupFilter!.combinator,
                filters: filter.groupFilter!.filters.map(f => cleanFilterFromCondition(f, condition)).filter(f => f != null) as Filter[]
            }
        }
    }
    return null;
}

// cleanGroupsFromCondition cleans the groups from the given condition
// It returns the groups that match the condition
export function cleanGroupsFromCondition(groups: Group[] | null, condition: (group: Group) => boolean): Group[] {
    if (!groups) {
        return [];
    }
    return groups.filter(g => condition(g));
}

export function hasFilter(filter: Filter | null | undefined, fieldKey: string, fieldByPathKeys: Map<string, Field>): boolean {

    if (!filter || (!isAnyGroupFilter(filter) && !isSingleFilter(filter))) {
        return false;
    }

    if (isSingleFilter(filter)) {
        if (!filter.singleFilter!.pathKey || !filter.singleFilter!.operator) {
            return false;
        }

        if (!filter.singleFilter!.value || !filter.singleFilter!.valueKey) {
            return fieldByPathKeys.get(filter.singleFilter!.pathKey)?._key === fieldKey;
        }
        return false;
    }

    if (isAnyGroupFilter(filter)) {

        if (!filter.groupFilter!.combinator) {
            return false;
        }

        if (!filter.groupFilter!.filters || filter.groupFilter!.filters.length === 0) {
            return false;
        }

        return filter.groupFilter!.filters.some(f => hasFilter(f, fieldKey, fieldByPathKeys));
    }
    return false;
}

export function validateFilters(filters: Filter[] | null, onSuccess: (newFilter: Filter | null) => void, onError: (error: CompozzError) => void) {

    const selectedView = useModelStore.getState().selectedView;
    const selectedObjectInfos = useModelStore.getState().selectedObjectInfos;
    const workspace = useModelStore.getState().selectedWorkspace;

    if (!workspace?._key) {
        console.error("Workspace key is not set");
        return;
    }

    let newViewFilter: Filter | null = null
    if (filters) {
        newViewFilter = {
            groupFilter: {
                filters: filters,
                combinator: AND
            },
            singleFilter: null
        }
    }

    const newFilter = cleanFilter(newViewFilter);
    const newView: View = {
        ...selectedView!,
        filter: newFilter
    }
    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
        if (response) {
            if (isCompozzError(response)) {
                onError(response);
            }
        } else {
            onSuccess(newFilter);
        }
    })
}

// IsDirectPath checks if the path is a direct path
// A direct path is a path that is either a path towards a root field or a path towards a linked field with is not a link field itselt (depth = 1)
export function isDirectPath(pathKey: string): boolean {
    const srcsParts = pathKey.split(SOURCE_SEPARATOR);
    if (srcsParts.length != 2) {
        return false
    }
    const path = srcsParts[1];
    const pathParts = path.split(PATH_SEPARATOR);
    if (pathParts.length > 2) {
        return false
    }
    return true;
}

// getDirectPathFromFiltersAndGroups returns the direct path from the filters and groups
export function getDirectPathFromFiltersAndGroups(filter: Filter | null | undefined, groups: Group[] | null): string[] {

    const result: string[] = [];

    if (filter && isGroupFilter(filter, AND)) {
        if (filter?.groupFilter?.filters && filter.groupFilter!.filters.length > 0) {
            filter.groupFilter!.filters.forEach(f => {
                if (isGroupFilter(f, OR)) {
                    const pathKey = f.groupFilter!.filters[0].singleFilter!.pathKey;
                    if (isDirectPath(pathKey)) {
                        result.push(pathKey);
                    }
                }
            });
        }
    }

    if (groups) {
        groups.forEach(g => {
            if (isDirectPath(g.pathKey)) {
                result.push(g.pathKey);
            }
        });
    }

    return result;
}

export function allowedToAddRecord(filter: Filter | null | undefined, fieldByPathKeys: Map<string, Field>, groups: Group[] | null, groupAggregations: Map<string, AggregationType>, fieldByKeys: Map<string, Field> | null): boolean {

    for (const group of groups ?? []) {
        const field = fieldByPathKeys.get(group.pathKey);
        if (!field) {
            continue;
        }

        if (groupAggregations.get(group.pathKey) && groupAggregations.get(group.pathKey) !== AggregationType.NO_AGGR) {
            return false;
        }

        if (field.type !== FieldType.Link) {
            continue;
        }

        const destFieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
        if (!destFieldKey) {
            return false;
        }
        const destField = fieldByKeys?.get(destFieldKey);
        if (!destField) {
            return false;
        }

        if (destField.type === FieldType.Link || destField.type === FieldType.Boolean || destField.type === FieldType.Choice || destField.type === FieldType.MultiChoice || destField.type === FieldType.User) {
            return false;
        }

    }

    if (!filter) {
        return true;
    }

    if (!isGroupFilter(filter, AND)) {
        return false;
    }

    if (!filter?.groupFilter?.filters || filter.groupFilter!.filters.length === 0) {
        return true;
    }

    const pathFilters = filter.groupFilter!.filters

    let result = true;
    pathFilters.forEach(f => {
        if (!result) {
            return;
        }
        // In case of a "different" filter, it should be a AND group filter
        if (!isGroupFilter(f, OR) && !isGroupFilter(f, AND)) {
            result = false;
            return;
        }
        if (f.groupFilter!.filters.length > 1) {
            const filter = f.groupFilter!.filters[0].singleFilter!;
            const field = fieldByPathKeys.get(filter.pathKey);
            if (!field) {
                result = false;
                return;
            }
            const values = f.groupFilter!.filters.map(f => f.singleFilter!.value);
            const chosenDefaultValue = field.defaultValue && values.some(v => {
                if (isArray(field.defaultValue) && field.defaultValue.length > 0 && v === field.defaultValue[0]) {
                    return true;
                }
                return v === field.defaultValue;
            });

            // If there is a filter with multiple values, we can add a field only if: 
            // - The field is a multi choice
            // - or the filter is not a "different" filter and the chosen value is not the default value of the field
            // That is: field.type === FieldType.MultiChoice || ((filter.operator === NOT_HAS || filter.operator === NOT_EQUAL || filter.operator === NOT_CONTAINS) && !chosenDefaultValue)
            if (field.type !== FieldType.MultiChoice && (filter.operator != NOT_HAS && filter.operator != NOT_EQUAL  && filter.operator != NOT_CONTAINS || chosenDefaultValue)) {
                result = false;
                return;
            }
        }
        if (f.groupFilter!.filters.length === 1) {
            if (!isSingleFilter(f.groupFilter!.filters[0])) {
                result = false;
                return;
            }
            const filter = f.groupFilter!.filters[0].singleFilter!;
            const field = fieldByPathKeys.get(filter.pathKey);
            if (!field) {
                result = false;
                return;
            }

            if ((field.type === FieldType.Choice || field.type === FieldType.MultiChoice)) {
                if (filter.operator !== HAS && filter.operator !== NOT_HAS) {
                    result = false;
                    return;
                }
                if (filter.operator === NOT_HAS) {
                    if (filter.value != null && filter.value === field.defaultValue) {
                        result = false;
                        return;
                    }
                }
            } else if (field.type === FieldType.Period) {
                if (filter.operator !== IN) {
                    result = false;
                    return;
                }
            } else if (field.type === FieldType.Link) {

                const destFieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
                if (!destFieldKey) {
                    result = false;
                    return;
                }
                const destField = fieldByKeys?.get(destFieldKey);
                if (!destField) {
                    result = false;
                    return;
                }

                if (destField.type === FieldType.Link || destField.type === FieldType.Boolean || destField.type === FieldType.Choice || destField.type === FieldType.MultiChoice) {
                    result = false;
                    return;
                }

                if (filter.operator !== EQUAL && filter.operator !== NOT_EQUAL) {
                    result = false;
                    return;
                }
                if (filter.operator === NOT_EQUAL && filter.value === null) {
                    result = false;
                    return;
                }
            } else {
                if (filter.operator !== EQUAL && filter.operator !== NOT_EQUAL) {
                    result = false;
                    return;
                }
                if (filter.operator === NOT_EQUAL) {
                    if (filter.value != null && filter.value === field.defaultValue) {
                        result = false;
                        return;
                    }
                }
            }
        }
    });


    return result;
}

export type FilterValues = {
    filterValues: Map<string, any>
    overridenLinkByValue?: Map<string, boolean>
}

export function getFilterValues(filter: Filter | null | undefined, fieldByPathKeys: Map<string, Field>, fieldByKeys: Map<string, Field> | null): FilterValues {

    const result: FilterValues = {
        filterValues: new Map(),
        overridenLinkByValue: undefined
    };
    if (!allowedToAddRecord(filter, fieldByPathKeys, null, new Map(), fieldByKeys)) {
        return result;
    }

    const filters = filter?.groupFilter?.filters;
    if (!filters) {
        return result;
    }
    for (const f of filters) {
        const filter = f.groupFilter?.filters[0];
        if (!filter) {
            continue;
        }
        const singleFilter = filter.singleFilter;
        const field = fieldByPathKeys.get(singleFilter!.pathKey);
        if (!field) {
            continue;
        }

        if (singleFilter!.operator === NOT_HAS || singleFilter!.operator === NOT_EQUAL) {
            continue;
        }

        if (field.type === FieldType.Choice) {
            if (singleFilter!.value) {
                result.filterValues.set(field._key, [singleFilter!.value]);
            }
        } else if (field.type === FieldType.MultiChoice) {
            let values: string[] = [];
            f.groupFilter?.filters.forEach(f => {
                if (f.singleFilter?.value) {
                    values.push(f.singleFilter?.value);
                }
            });
            if (values.length > 0) {
                result.filterValues.set(field._key, values);
            }
        } else if (field.type === FieldType.Link) {
            if (singleFilter!.valueKey) {
                result.filterValues.set(field._key, singleFilter!.valueKey);
            } else if (singleFilter!.formula === USER_FORMULA) {
                const currentUser = useUserStore.getState().user;
                if (currentUser) {
                    result.filterValues.set(field._key, currentUser._key);
                    let overridenLinkByValue = result.overridenLinkByValue;
                    if (!overridenLinkByValue) {
                        overridenLinkByValue = new Map();
                    }
                    const relationKey = field.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
                    if (relationKey) {
                        overridenLinkByValue.set(relationKey, true);
                    }
                    result.overridenLinkByValue = overridenLinkByValue;
                }
            }
        } else {
            if (singleFilter!.value) {
                result.filterValues.set(field._key, singleFilter!.value);
            }
        }
    };
    return result;
}

export function hasFilterOnPath(filter: Filter | null | undefined, pathKey: string): boolean {
    if (!filter) {
        return false;
    }
    if (isSingleFilter(filter)) {
        return filter.singleFilter!.pathKey === pathKey;
    }
    if (isAnyGroupFilter(filter)) {
        return filter.groupFilter!.filters.some(f => hasFilterOnPath(f, pathKey));
    }
    return false;
}